import React, { useMemo, useState } from 'react';
import { ArrowLeft, Check, Loader2 } from 'lucide-react';
import {
  Button,
  Checkbox,
  DatePicker,
  Input,
  Label,
  Textarea,
} from '@roger/ui-components';
import type {
  FdbMedicationResult,
  ImportedAllergyLineResponse,
  ImportedMedicationLineResponse,
} from '../../services/api';
import {
  declineImportedAllergyLine,
  declineImportedMedicationLine,
  reconcileImportedAllergyLine,
  reconcileImportedMedicationLine,
} from '../../services/api';
import MedicationSearch from '../medications/MedicationSearch';
import AllergenSearch, { type SelectedAllergen } from './AllergenSearch';
import {
  ImportedAllergyWasPanel,
  ImportedMedicationWasPanel,
} from './ImportedLineWasPanel';
import {
  AMOUNT_NA_HELP,
  AMOUNT_NA_LABEL,
  DONT_IMPORT_LABEL,
  NOT_CARRIED_REASON_HELP,
} from './importedChartLineCopy';
import {
  businessDateToday,
  formatDateToISO,
  formatDateToMMDDYYYY,
  parseISOToLocalDate,
} from '../../utils/dateUtils';
import { getDosageUnit } from '../medications/medicationUtils';
import { rulingErrorMessage } from './importedChartLines';
import { useToast } from '../../hooks/useToastContext';

type ImportedLine =
  | ImportedMedicationLineResponse
  | ImportedAllergyLineResponse;

type Step = 'entry' | 'review' | 'decline';

/**
 * Which kind of line this is.
 *
 * Keyed on the registry-key column rather than on `allergen`, because that
 * column is required on an allergy line and optional-nullable everywhere else —
 * a guard on an optional field narrows only by accident. `source` cannot do
 * this job: both types carry the same `"kinnser_import"` literal, which says
 * where the row came from, not what it is.
 */
function isAllergyLine(
  line: ImportedLine
): line is ImportedAllergyLineResponse {
  return 'patient_medispan_allergy_registry_key' in line;
}

function lineTitle(line: ImportedLine): string {
  if (isAllergyLine(line)) {
    return line.allergen || 'Unnamed allergen';
  }
  return line.name || line.title || line.order_string || 'Unnamed medication';
}

/** Best-effort start-date carryover; the coding identities are typed fresh. */
function startDateFromLine(line: ImportedLine | null): Date | undefined {
  if (!line || isAllergyLine(line) || !line.start_date) {
    return undefined;
  }
  return parseISOToLocalDate(line.start_date);
}

/**
 * Whether Kinnser recorded a stop for this line. Either signal counts: a
 * discontinued status, or a recorded discontinue date — including a future
 * one, which imports as Roger's future-discontinue state (still active, with
 * a planned stop).
 */
function lineHasDiscontinueDate(line: ImportedLine | null): boolean {
  if (!line || isAllergyLine(line)) {
    return false;
  }
  return (
    line.status?.trim().toLowerCase() === 'discontinued' ||
    Boolean(line.discontinue_date)
  );
}

/** Mirrors `isActuallyDiscontinued`: a stop today or earlier has happened. */
function stopHasHappened(stopDate: Date | undefined): boolean {
  if (!stopDate) {
    return false;
  }
  return stopDate <= businessDateToday();
}

function discontinueDateFromLine(line: ImportedLine | null): Date | undefined {
  if (!line || isAllergyLine(line) || !line.discontinue_date) {
    return undefined;
  }
  return parseISOToLocalDate(line.discontinue_date);
}

function reactionFromLine(line: ImportedLine | null): string {
  return line && isAllergyLine(line) ? (line.reaction ?? '') : '';
}

/**
 * The Patient Data Importer: where a scraped sentence becomes a Roger record.
 *
 * **It replaces the list rather than floating over it.** Reconciling is not a
 * quick edit — it is reading one chart and writing another, one line at a
 * time — and a dialog over the list would put the thing being read behind the
 * thing being written.
 *
 * **Two panels, and the left one is the evidence.** "Was" holds Kinnser's
 * verbatim text; "Now" is a full structured entry the clinician fills in
 * themselves. The coding identities — the drug, the allergen — are never
 * pre-filled: the parse is unreliable, so a pre-filled pick would be a guess
 * wearing the clinician's authority. Administrative facts carry over best
 * effort instead (start date, discontinued state and date, an allergy's
 * reaction text); each is re-shown on the review step before anything writes.
 *
 * **A review step before the write.** "Was / Now" side by side, because the
 * failure this feature must not have is a line silently becoming the wrong
 * drug. One more look is cheap; a wrong medication on a chart is not.
 *
 * **Two verbs, never one.** A line can be imported or explicitly not
 * imported — and declining takes a reason, so the imported record itself
 * explains why a drug from the old chart is absent from this one. The verb is
 * "import", not "carry forward": discontinued medications are imported too.
 */
const PatientDataImporterTool: React.FC<{
  patientId: string;
  domain: 'medication' | 'allergy';
  lines: ImportedLine[];
  onClose: () => void;
  /** Called after any successful ruling so the caller refetches both lists. */
  onChanged: () => void;
}> = ({ patientId, domain, lines, onClose, onChanged }) => {
  const { error: showError, success } = useToast();
  const [selectedId, setSelectedId] = useState<string | null>(
    lines[0]?.id ?? null
  );
  const [step, setStep] = useState<Step>('entry');
  const [isSaving, setIsSaving] = useState(false);

  // "Now" side — medication
  const [selectedMedication, setSelectedMedication] =
    useState<FdbMedicationResult | null>(null);
  const [startDate, setStartDate] = useState<Date | undefined>(() =>
    startDateFromLine(lines[0] ?? null)
  );
  const [amount, setAmount] = useState('');
  // "Amount N/A" (2026-08-28 ruling): a blank field is ambiguous — forgot,
  // or absent? — so recording without an amount is a checked assertion, the
  // same distinction NKA draws against an empty allergy list.
  const [amountNa, setAmountNa] = useState(false);
  const [frequencyInstructions, setFrequencyInstructions] = useState('');
  // Without this pair, a stopped drug could only be imported as active — a
  // dead end that would leave discontinued lines stranded as flagged imports.
  // A future date is legitimate too: it lands as future-discontinue (active,
  // planned stop), which every date-aware Roger subsystem handles.
  const [hasDiscontinueDate, setHasDiscontinueDate] = useState(() =>
    lineHasDiscontinueDate(lines[0] ?? null)
  );
  const [discontinueDate, setDiscontinueDate] = useState<Date | undefined>(() =>
    discontinueDateFromLine(lines[0] ?? null)
  );

  // Fields the user has visited. A visited field left invalid gets an error
  // border, so the disabled Review button has a visible reason.
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const markTouched = (field: string) =>
    setTouched(prev => ({ ...prev, [field]: true }));

  // "Now" side — allergy
  const [selectedAllergen, setSelectedAllergen] =
    useState<SelectedAllergen | null>(null);
  const [reaction, setReaction] = useState(() =>
    reactionFromLine(lines[0] ?? null)
  );

  const [declineReason, setDeclineReason] = useState('');

  const selectedLine = useMemo(
    () => lines.find(line => line.id === selectedId) ?? null,
    [lines, selectedId]
  );

  const isMedication = domain === 'medication';

  const resetEntry = (nextLine: ImportedLine | null) => {
    setSelectedMedication(null);
    setStartDate(startDateFromLine(nextLine));
    setAmount('');
    setAmountNa(false);
    setFrequencyInstructions('');
    setHasDiscontinueDate(lineHasDiscontinueDate(nextLine));
    setDiscontinueDate(discontinueDateFromLine(nextLine));
    setSelectedAllergen(null);
    setReaction(reactionFromLine(nextLine));
    setDeclineReason('');
    setTouched({});
    setStep('entry');
  };

  const selectLine = (id: string) => {
    setSelectedId(id);
    resetEntry(lines.find(line => line.id === id) ?? null);
  };

  // An amount is required unless its absence is asserted: the N/A checkbox
  // makes the skip a statement rather than an oversight. Zero of a
  // medication is not a statement anyone means to make — a given amount
  // must be positive.
  const amountValid =
    amountNa || (Boolean(amount.trim()) && parseFloat(amount) > 0);

  // Mirrors the server's validator so a backwards date pair is a visible
  // field error here rather than a 422 toast at confirm.
  const discontinueOrderValid =
    !hasDiscontinueDate ||
    !discontinueDate ||
    !startDate ||
    discontinueDate >= startDate;

  // Every field Roger itself requires must be present before the review step.
  // Administrative facts may arrive from the line; the coding identities are
  // complete only because the clinician picked them.
  const entryComplete = isMedication
    ? Boolean(
        selectedMedication &&
          startDate &&
          frequencyInstructions.trim() &&
          amountValid &&
          (!hasDiscontinueDate || discontinueDate) &&
          discontinueOrderValid
      )
    : Boolean(selectedAllergen && reaction.trim());

  // The visible counterpart of the disabled Review button.
  const fieldError = {
    medication: Boolean(touched.medication && !selectedMedication),
    startDate: Boolean(touched.startDate && !startDate),
    amount: Boolean(touched.amount && !amountValid),
    frequency: Boolean(touched.frequency && !frequencyInstructions.trim()),
    discontinueDate: Boolean(
      (touched.discontinueDate && hasDiscontinueDate && !discontinueDate) ||
        (discontinueDate && !discontinueOrderValid)
    ),
    allergen: Boolean(touched.allergen && !selectedAllergen),
    reaction: Boolean(touched.reaction && !reaction.trim()),
  };

  const dosageUnit = getDosageUnit(
    selectedMedication,
    amount ? parseFloat(amount) : undefined
  );

  const advanceAfterRuling = () => {
    onChanged();
    const remaining = lines.filter(line => line.id !== selectedId);
    if (remaining.length === 0) {
      onClose();
      return;
    }
    setSelectedId(remaining[0].id);
    resetEntry(remaining[0]);
  };

  const handleConfirm = async () => {
    if (!selectedLine) return;
    setIsSaving(true);
    try {
      // The generated client resolves with a populated `error` rather than
      // throwing, so every failure is checked explicitly.
      const response = isMedication
        ? await reconcileImportedMedicationLine({
            path: { patient_id: patientId, line_id: selectedLine.id },
            body: {
              start_date: formatDateToISO(startDate!),
              discontinue_date:
                hasDiscontinueDate && discontinueDate
                  ? formatDateToISO(discontinueDate)
                  : undefined,
              amount: amountNa || !amount ? undefined : parseFloat(amount),
              frequency_instructions: frequencyInstructions.trim(),
              fdb_dispensable_drug_id:
                selectedMedication!.fdb_dispensable_drug_id,
            },
          })
        : await reconcileImportedAllergyLine({
            path: { patient_id: patientId, line_id: selectedLine.id },
            body: {
              fdb_allergen_id: selectedAllergen!.fdbAllergenId,
              fdb_allergen_name: selectedAllergen!.name,
              reaction: reaction.trim(),
            },
          });

      if (response.error) {
        showError(
          rulingErrorMessage(
            response.error,
            'Unable to import this record. Please try again.'
          )
        );
        return;
      }
      success(`Imported: ${lineTitle(selectedLine)}`);
      advanceAfterRuling();
    } finally {
      setIsSaving(false);
    }
  };

  const handleDecline = async () => {
    if (!selectedLine || !declineReason.trim()) return;
    setIsSaving(true);
    try {
      const response = isMedication
        ? await declineImportedMedicationLine({
            path: { patient_id: patientId, line_id: selectedLine.id },
            body: { reason: declineReason.trim() },
          })
        : await declineImportedAllergyLine({
            path: { patient_id: patientId, line_id: selectedLine.id },
            body: { reason: declineReason.trim() },
          });

      if (response.error) {
        showError(
          rulingErrorMessage(
            response.error,
            'Unable to record that decision. Please try again.'
          )
        );
        return;
      }
      success(`Not imported: ${lineTitle(selectedLine)}`);
      advanceAfterRuling();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div data-testid='patient-data-importer-tool'>
      <div className='mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between'>
        <div>
          <h2 className='text-lg font-semibold'>Patient Data Importer</h2>
          <p className='text-sm text-muted-foreground'>
            {lines.length} imported {isMedication ? 'medication' : 'allergy'}{' '}
            record{lines.length === 1 ? '' : 's'} to review.
          </p>
        </div>
        <Button variant='outline' size='sm' onClick={onClose}>
          <ArrowLeft className='h-3.5 w-3.5 mr-1.5' />
          Back to {isMedication ? 'medications' : 'allergies'}
        </Button>
      </div>

      <div className='flex flex-col gap-4 md:flex-row'>
        {/* The queue. Kept visible so a clinician can see how much is left and
            jump between lines rather than being marched through them. */}
        <aside className='w-full md:w-64 shrink-0'>
          <ul className='space-y-1' data-testid='importer-line-list'>
            {lines.map(line => (
              <li key={line.id}>
                <button
                  type='button'
                  onClick={() => selectLine(line.id)}
                  className={`w-full rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                    line.id === selectedId
                      ? 'border-amber-300 bg-amber-50 text-amber-900'
                      : 'hover:bg-accent'
                  }`}
                >
                  <span className='block truncate font-medium'>
                    {lineTitle(line)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <div className='min-w-0 flex-1'>
          {!selectedLine ? (
            <p className='text-sm text-muted-foreground'>
              Nothing left to review.
            </p>
          ) : (
            <div className='grid grid-cols-1 gap-4 xl:grid-cols-2'>
              {/* Was and Now go side by side at xl:, not lg:. Between roughly
                  1024 and 1130px each column is narrow enough that the entry
                  form's Start-date / Amount row (sm:grid-cols-2) overflows its
                  cell. One breakpoint later the columns are wide enough to
                  hold it. */}
              <section className='rounded-md border p-4'>
                <h3 className='mb-3 text-sm font-semibold'>Was</h3>
                {isAllergyLine(selectedLine) ? (
                  <ImportedAllergyWasPanel line={selectedLine} />
                ) : (
                  <ImportedMedicationWasPanel
                    line={selectedLine}
                    onCopySig={setFrequencyInstructions}
                  />
                )}
              </section>

              <section className='rounded-md border p-4'>
                <h3 className='mb-3 text-sm font-semibold'>Now</h3>

                {step === 'decline' ? (
                  <div className='space-y-3' data-testid='decline-panel'>
                    <Label htmlFor='decline-reason'>Reason</Label>
                    <Textarea
                      id='decline-reason'
                      rows={4}
                      value={declineReason}
                      onChange={e => setDeclineReason(e.target.value)}
                      placeholder='Why is this record not being imported?'
                    />
                    <p className='text-xs text-muted-foreground'>
                      {NOT_CARRIED_REASON_HELP}
                    </p>
                    <div className='flex flex-wrap gap-2'>
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={() => setStep('entry')}
                        disabled={isSaving}
                      >
                        Cancel
                      </Button>
                      <Button
                        size='sm'
                        onClick={handleDecline}
                        disabled={isSaving || !declineReason.trim()}
                        data-testid='confirm-decline'
                      >
                        {isSaving && (
                          <Loader2 className='h-3.5 w-3.5 mr-1.5 animate-spin' />
                        )}
                        Confirm
                      </Button>
                    </div>
                  </div>
                ) : step === 'review' ? (
                  <div className='space-y-3' data-testid='review-panel'>
                    <p className='text-sm text-muted-foreground'>
                      Check the record you are about to create against the
                      imported line.
                    </p>
                    <dl className='space-y-2 rounded-md border bg-muted/30 p-3'>
                      {isMedication ? (
                        <>
                          <div>
                            <dt className='text-xs uppercase tracking-wide text-muted-foreground'>
                              Medication
                            </dt>
                            <dd className='text-sm font-medium'>
                              {selectedMedication?.full_name}
                            </dd>
                          </div>
                          <div>
                            <dt className='text-xs uppercase tracking-wide text-muted-foreground'>
                              Start date
                            </dt>
                            <dd className='text-sm'>
                              {startDate ? formatDateToMMDDYYYY(startDate) : ''}
                            </dd>
                          </div>
                          {hasDiscontinueDate && discontinueDate && (
                            <div>
                              {/* A stop that has happened reads red; a
                                  planned one is a neutral fact — the row
                                  imports as active until the date. */}
                              <dt className='text-xs uppercase tracking-wide text-muted-foreground'>
                                {stopHasHappened(discontinueDate)
                                  ? 'Discontinued'
                                  : 'Discontinues'}
                              </dt>
                              <dd
                                className={
                                  stopHasHappened(discontinueDate)
                                    ? 'text-sm font-medium text-destructive'
                                    : 'text-sm'
                                }
                              >
                                {formatDateToMMDDYYYY(discontinueDate)}
                              </dd>
                            </div>
                          )}
                          {(amount || amountNa) && (
                            <div>
                              <dt className='text-xs uppercase tracking-wide text-muted-foreground'>
                                Amount
                              </dt>
                              <dd className='text-sm'>
                                {amountNa ? 'N/A' : amount}
                              </dd>
                            </div>
                          )}
                          <div>
                            <dt className='text-xs uppercase tracking-wide text-muted-foreground'>
                              Frequency / instructions
                            </dt>
                            <dd className='text-sm'>{frequencyInstructions}</dd>
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <dt className='text-xs uppercase tracking-wide text-muted-foreground'>
                              Allergen
                            </dt>
                            <dd className='text-sm font-medium'>
                              {selectedAllergen?.name}
                            </dd>
                          </div>
                          <div>
                            <dt className='text-xs uppercase tracking-wide text-muted-foreground'>
                              Reaction
                            </dt>
                            <dd className='text-sm'>{reaction}</dd>
                          </div>
                        </>
                      )}
                    </dl>
                    <div className='flex flex-wrap gap-2'>
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={() => setStep('entry')}
                        disabled={isSaving}
                      >
                        Back
                      </Button>
                      <Button
                        size='sm'
                        onClick={handleConfirm}
                        disabled={isSaving}
                        data-testid='confirm-reconcile'
                      >
                        {isSaving ? (
                          <Loader2 className='h-3.5 w-3.5 mr-1.5 animate-spin' />
                        ) : (
                          <Check className='h-3.5 w-3.5 mr-1.5' />
                        )}
                        Confirm import
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className='space-y-3' data-testid='entry-panel'>
                    {isMedication ? (
                      <>
                        <div>
                          <Label>Medication</Label>
                          <div
                            onBlur={() => markTouched('medication')}
                            className={
                              fieldError.medication
                                ? 'rounded-md ring-1 ring-destructive'
                                : undefined
                            }
                          >
                            {/* Keyed by line: the search owns its typed text,
                                so without a remount a stale query (and its
                                results) would survive a line switch — a
                                wrong-drug hazard. */}
                            <MedicationSearch
                              key={selectedLine.id}
                              value={selectedMedication}
                              onSelect={setSelectedMedication}
                              onClear={() => setSelectedMedication(null)}
                              placeholder='Search medications...'
                            />
                          </div>
                        </div>
                        <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
                          <div>
                            <Label>Start date</Label>
                            <div onBlur={() => markTouched('startDate')}>
                              <DatePicker
                                date={startDate}
                                onDateChange={setStartDate}
                                placeholder='Select start date'
                                datePickerSize='default'
                                className={
                                  fieldError.startDate
                                    ? 'border-destructive'
                                    : undefined
                                }
                              />
                            </div>
                          </div>
                          <div>
                            <Label htmlFor='reconcile-amount'>Amount</Label>
                            <div className='flex items-center gap-2'>
                              <Input
                                id='reconcile-amount'
                                type='number'
                                min='1'
                                value={amount}
                                onChange={e => setAmount(e.target.value)}
                                onBlur={() => markTouched('amount')}
                                disabled={amountNa}
                                inputSize='default'
                                className={
                                  fieldError.amount
                                    ? 'w-20 border-destructive'
                                    : 'w-20'
                                }
                              />
                              {dosageUnit && (
                                <span className='text-sm text-muted-foreground whitespace-nowrap'>
                                  {dosageUnit}
                                </span>
                              )}
                            </div>
                            <div className='mt-1.5 flex items-center gap-2'>
                              <Checkbox
                                id='reconcile-amount-na'
                                checked={amountNa}
                                onCheckedChange={checked => {
                                  const value = checked === true;
                                  setAmountNa(value);
                                  if (value) setAmount('');
                                  markTouched('amount');
                                }}
                                aria-label={AMOUNT_NA_LABEL}
                                data-testid='reconcile-amount-na'
                              />
                              <Label
                                htmlFor='reconcile-amount-na'
                                className='text-xs font-normal cursor-pointer'
                                title={AMOUNT_NA_HELP}
                              >
                                {AMOUNT_NA_LABEL}
                              </Label>
                            </div>
                          </div>
                        </div>
                        <div className='space-y-2'>
                          <div className='flex items-center gap-2'>
                            <Checkbox
                              id='reconcile-discontinued'
                              checked={hasDiscontinueDate}
                              onCheckedChange={checked => {
                                setHasDiscontinueDate(checked === true);
                                // Unchecking withdraws the stop; the pair
                                // must never disagree.
                                if (checked !== true) {
                                  setDiscontinueDate(undefined);
                                }
                              }}
                            />
                            <Label htmlFor='reconcile-discontinued'>
                              Has a discontinue date
                            </Label>
                          </div>
                          <div>
                            <Label>Discontinue date</Label>
                            <div onBlur={() => markTouched('discontinueDate')}>
                              <DatePicker
                                date={discontinueDate}
                                onDateChange={date => {
                                  setDiscontinueDate(date);
                                  // Entering a date IS asserting a stop, so
                                  // the box checks itself.
                                  if (date) {
                                    setHasDiscontinueDate(true);
                                  }
                                }}
                                placeholder='Select discontinue date'
                                datePickerSize='default'
                                className={
                                  fieldError.discontinueDate
                                    ? 'border-destructive'
                                    : undefined
                                }
                              />
                            </div>
                          </div>
                        </div>
                        <div>
                          <Label htmlFor='reconcile-frequency'>
                            Frequency / instructions
                          </Label>
                          <Textarea
                            id='reconcile-frequency'
                            rows={2}
                            value={frequencyInstructions}
                            onChange={e =>
                              setFrequencyInstructions(e.target.value)
                            }
                            onBlur={() => markTouched('frequency')}
                            placeholder='Enter frequency and instructions (Required)'
                            className={
                              fieldError.frequency
                                ? 'border-destructive'
                                : undefined
                            }
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <Label>Allergen</Label>
                          <div
                            onBlur={() => markTouched('allergen')}
                            className={
                              fieldError.allergen
                                ? 'rounded-md ring-1 ring-destructive'
                                : undefined
                            }
                          >
                            {/* Keyed for the same wrong-pick hazard as the
                                medication search above. */}
                            <AllergenSearch
                              key={selectedLine.id}
                              value={selectedAllergen}
                              onSelect={setSelectedAllergen}
                              onClear={() => setSelectedAllergen(null)}
                            />
                          </div>
                        </div>
                        <div>
                          <Label htmlFor='reconcile-reaction'>Reaction</Label>
                          <Input
                            id='reconcile-reaction'
                            value={reaction}
                            onChange={e => setReaction(e.target.value)}
                            onBlur={() => markTouched('reaction')}
                            placeholder='Enter reaction... (Required)'
                            inputSize='default'
                            className={
                              fieldError.reaction
                                ? 'border-destructive'
                                : undefined
                            }
                          />
                        </div>
                      </>
                    )}

                    <div className='flex flex-wrap gap-2 pt-1'>
                      <Button
                        size='sm'
                        onClick={() => setStep('review')}
                        disabled={!entryComplete}
                        data-testid='review-was-now'
                      >
                        {/* The verb names the outcome. A past stop lands
                            discontinued; a future one lands active with a
                            planned stop, so it reads as a plain import. */}
                        {isMedication &&
                        hasDiscontinueDate &&
                        stopHasHappened(discontinueDate)
                          ? 'Import discontinued'
                          : 'Import'}
                      </Button>
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={() => setStep('decline')}
                        data-testid='start-decline'
                      >
                        {DONT_IMPORT_LABEL}
                      </Button>
                    </div>
                  </div>
                )}
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatientDataImporterTool;
